import {useCallback, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {isEqual, isEmpty} from 'lodash';
import queryString from 'query-string';
import {useEngagement, useEngagementId} from '../../util/customHooks';
import {getTaskDocumentByDocumentId} from '../../selectors/Ares/selectors';
import {getCurrentMateriality, getPriorYearMateriality} from '../../selectors/Ares/materiality';
import {getMateriality} from '../../actions/Materiality/materialityactions';

/**
 * Custom hook to provide a service to interact with materiality data.
 * @param {String} documentId Document id.
 * @param {Boolean} includeMestSwitcher Indicates if the MEST switcher is used when infering the selected stEntityId.
 * @returns {Object} Materiality service with methods.
 */
export const useMaterialityService = (documentId, includeMestSwitcher = false) => {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();

	const stEntityIdStore = useSelector((state) => (includeMestSwitcher ? state.stEntityId : {}), isEqual);

	/**
	 * Prior year materiality from the store.
	 */
	const priorYearMateriality = useSelector(getPriorYearMateriality, isEqual);

	/**
	 * Current materiality from Redux store.
	 */
	const currentMateriality = useSelector(getCurrentMateriality, isEqual);

	const isMultiEntityEngagement = useEngagement()?.isMultiEntityEngagement;

	/**
	 * Gets the current document data from the store.
	 * @return {Object} The document data.
	 */
	const documentData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId), isEqual);

	const baseMaterialityServiceInstance = useMemo(() => {
		return getBaseMaterialityService(engagementId, documentId, documentData, isMultiEntityEngagement, stEntityIdStore);
	}, [engagementId, documentId, documentData, isMultiEntityEngagement, stEntityIdStore]);

	const selectedStEntityId = baseMaterialityServiceInstance.getSelectedStEntityId();

	/**
	 * Fetches all materiality data for the engagement, optionally filtered by the selected stEntityId.
	 * @param {Boolean} trackWithArea Indicates if the fetch should consider area tracking.
	 * @param {Number | String} stEntityId Optional stEntityId to filter the materiality data.
	 * @param {Object} additionalQueryParams Additional query parameters to include in the request.
	 * @returns {Promise} Promise resolving to the fetched materiality data.
	 */
	const getAllMateriality = useCallback(
		(trackWithArea, stEntityId = selectedStEntityId, additionalQueryParams = {}) => {
			const queryParams = {
				stEntityId,
				...additionalQueryParams
			};
			return dispatch(getMateriality(engagementId, trackWithArea, queryParams));
		},
		[dispatch, engagementId, selectedStEntityId]
	);

	// Return the materiality service instance along with the getAllMateriality method.
	return useMemo(() => {
		return {
			...baseMaterialityServiceInstance,
			priorYearMateriality,
			currentMateriality,
			getAllMateriality
		};
	}, [baseMaterialityServiceInstance, getAllMateriality, priorYearMateriality, currentMateriality]);
};

/**
 * Basic materiality service, without any React / Redux dependencies.
 * @param {String | Number} engagementId Engagement id.
 * @param {String} documentId Document id.
 * @param {*} documentData Document data.
 * @param {Boolean} isMultiEntityEngagement Indicates if the engagement is multi-entity.
 * @param {Object} stEntityIdStore StEntityId selected from the MEST switcher.
 * @returns {Object} Materiality service with methods.
 */
export const getBaseMaterialityService = (engagementId, documentId, documentData, isMultiEntityEngagement, stEntityIdStore) => {
	// Normalize documentData. If entities is an array of objects, convert it to an array of stEntityIds.
	const normalizedDocumentData = {...documentData};
	if (Array.isArray(documentData?.entities) && documentData.entities.length > 0 && typeof documentData.entities[0] === 'object') {
		normalizedDocumentData.entities = documentData.entities.map((entity) => entity.id);
	}

	const getStEntityIdFromUrl = () => {
		let {stentityid: urlStEntityId} = queryString.parse(window.location.search.toLowerCase());

		if (!isEmpty(stEntityIdStore)) {
			urlStEntityId = stEntityIdStore.currentValue;
		}
		return urlStEntityId ? Number(urlStEntityId) : null;
	};

	/**
	 * Checks if the document is related to a specific entity.
	 * @param {Number | String} stEntityId The stEntityId to check.
	 * @returns {Boolean} True if the document is related to the entity, false otherwise.
	 */
	const isDocumentRelatedToEntity = (stEntityId) => {
		if (!isMultiEntityEngagement) {
			return false;
		}
		// A document with no STEntityDocument records applies to the whole engagement, so it stays
		// related to whichever entity is selected and the engagement wide materiality is shown
		// instead of the placeholder.
		if ((normalizedDocumentData?.entities || []).length === 0) {
			return true;
		}

		const entityExistsInDocument = normalizedDocumentData.entities.some((id) => id === Number(stEntityId));
		return entityExistsInDocument;
	};

	return {
		/**
		 * Checks if the document's stEntity relationship is valid based on the url and document data.
		 * @returns {Boolean} True if the relationship is valid, false otherwise.
		 */
		isDocumentStEntityRelationshipValid: () => {
			if (!isMultiEntityEngagement) {
				return true;
			}

			const urlStEntityId = getStEntityIdFromUrl();
			if (urlStEntityId) {
				return isDocumentRelatedToEntity(urlStEntityId);
			} else {
				return true;
			}
		},

		/**
		 * Gets the stEntityId from the URL.
		 * @returns {Number | null} The stEntityId from the URL or null if not found.
		 */
		getStEntityIdFromUrl,

		/**
		 * Gets the selected stEntityId based on the url or document data.
		 * @returns {Number | null} The selected stEntityId or null if not found.
		 */
		getSelectedStEntityId: () => {
			let selectedStEntityId = null;

			if (!isMultiEntityEngagement || (normalizedDocumentData?.entities ?? []).length === 0) {
				return selectedStEntityId;
			}

			// 1. Get the stEntityId from the url
			const urlStEntityId = getStEntityIdFromUrl();

			// 2. Check if the url stEntityId exists in documentData's entities list
			if (urlStEntityId) {
				const entityExistsInDocument = normalizedDocumentData.entities.some((stEntityId) => stEntityId === Number(urlStEntityId));

				if (entityExistsInDocument) {
					selectedStEntityId = Number(urlStEntityId);
				}
			} else {
				// No stEntityId in url, use the first one from documentData's entities list
				selectedStEntityId = normalizedDocumentData.entities[0];
			}

			return selectedStEntityId;
		}
	};
};
